import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { SugarDaddyProfile, LifestyleInterestTag, SdSupportTag, ArrangementTypeTag } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import MediaUpload from './MediaUpload';
import { FileWithPath } from 'react-dropzone';

// --- Constants for Checkbox Options ---
// Définition explicite des tableaux littéraux pour Zod
const lifestyleTags = ['studies', 'professional', 'hobbies', 'travel', 'gastronomy', 'artsCulture', 'luxury', 'sports', 'wellness'] as const;
const sdSupportTags = ['mentorship', 'generousSupport', 'needsBasedSupport', 'gifts', 'travel', 'eventsAccess', 'networkingAccess', 'sharedExperiences'] as const;
const arrangementTypeTags = ['mentorship', 'financialSupport', 'experiences', 'networking', 'shortTerm', 'longTerm', 'platonic', 'discreet', 'public_limited', 'eventsCompanion', 'regularDates'] as const;

// --- Zod Schema Definition ---
const sugarDaddyProfileSchema = z.object({
  pseudo: z.string().min(2, { message: "Pseudo must be at least 2 characters." }),
  ageRange: z.string().regex(/^\d{2}-\d{2}$/, { message: "Format d'âge invalide (ex: 35-45)." }),
  profession: z.string().optional(),
  location: z.object({
    city: z.string().min(1, { message: "City is required." }),
    country: z.string().min(1, { message: "Country is required." }),
  }),
  description: z.string().min(10, { message: "Description must be at least 10 characters." }),
  lifestyleInterests: z.array(z.enum(lifestyleTags)).min(1, { message: "Select at least one interest." }),
  offeredArrangement: z.object({
    supportType: z.array(z.enum(sdSupportTags)).min(1, { message: "Select at least one support type." }),
    mentoringField: z.string().optional(),
    arrangementType: z.array(z.enum(arrangementTypeTags)).min(1, { message: "Select at least one arrangement type." }),
  }),
  // verificationStatus is handled by admin
  // TODO: Add fields for photos/videos later
});

type SugarDaddyProfileFormData = z.infer<typeof sugarDaddyProfileSchema>;

// --- Component Props Interface ---
interface SugarDaddyProfileFormProps {
  onSubmit: (data: SugarDaddyProfileFormData, filesToUpload: FileWithPath[]) => Promise<void>;
  initialData?: Partial<SugarDaddyProfile>;
  isSubmitting?: boolean;
  initialMedia?: { photos?: { url: string; name: string }[] };
}

// --- Form Component ---
const SugarDaddyProfileForm: React.FC<SugarDaddyProfileFormProps> = ({ onSubmit, initialData, isSubmitting, initialMedia }) => {
  const { t } = useTranslation(['sugar', 'forms', 'common']);
  const [photoFiles, setPhotoFiles] = useState<FileWithPath[]>([]);

  const { control, register, handleSubmit, watch, formState: { errors } } = useForm<SugarDaddyProfileFormData>({
    resolver: zodResolver(sugarDaddyProfileSchema),
    defaultValues: {
      pseudo: initialData?.pseudo || '',
      ageRange: initialData?.ageRange || '',
      profession: initialData?.profession || '',
      location: initialData?.location || { city: '', country: '' },
      description: initialData?.description || '',
      lifestyleInterests: initialData?.lifestyleInterests || [],
      offeredArrangement: {
        supportType: initialData?.offeredArrangement?.supportType || [],
        mentoringField: initialData?.offeredArrangement?.mentoringField || '',
        arrangementType: initialData?.offeredArrangement?.arrangementType || [],
      },
    },
  });

  const watchSupportType = watch("offeredArrangement.supportType", []);

  const renderCheckboxGroup = (fieldName: any, options: readonly string[]) => (
      <div className="space-y-2">
          {options.map((option) => (
              <Controller
                  key={option}
                  name={fieldName}
                  control={control}
                  render={({ field }) => {
                      const currentValues = field.value || [];
                      return (
                          <div className="flex items-center space-x-2">
                              <Checkbox
                                  id={`${fieldName}-${option}`}
                                  checked={currentValues.includes(option)}
                                  onCheckedChange={(checked) => {
                                      const newValue = checked
                                          ? [...currentValues, option]
                                          : currentValues.filter((v: string) => v !== option);
                                      field.onChange(newValue);
                                  }}
                              />
                              <Label htmlFor={`${fieldName}-${option}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                   {t(`sugar:tags.${fieldName.split('.')[0] === 'offeredArrangement' ? (fieldName.split('.')[1] === 'supportType' ? 'sd_support' : 'arrangement') : fieldName.split('.')[0]}.${option}`, option)}
                              </Label>
                         </div>
                      );
                  }}
              />
          ))}
          {/* Accéder aux erreurs imbriquées avec ?. et s'assurer que message existe */}
          { (errors as any)[fieldName.split('.')[0]]?.[fieldName.split('.')[1]]?.message && 
            <p className="mt-1 text-xs text-red-600">{(errors as any)[fieldName.split('.')[0]]?.[fieldName.split('.')[1]]?.message}</p>}
            
          {/* Afficher l'erreur de l'array si elle existe directement sur fieldName */}
          { (errors as any)[fieldName]?.message && !fieldName.includes('.') && 
            <p className="mt-1 text-xs text-red-600">{(errors as any)[fieldName]?.message}</p>}    
      </div>
  );

  const handleFormSubmit = (formData: SugarDaddyProfileFormData) => {
      onSubmit(formData, photoFiles);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
      {/* Pseudo */}
      <div>
        <Label htmlFor="pseudo">{t('forms:label_pseudo')}</Label>
        <Input id="pseudo" {...register('pseudo')} />
        {errors.pseudo && <p className="mt-1 text-xs text-red-600">{errors.pseudo.message}</p>}
      </div>

      {/* Age Range */}
       <div>
        <Label htmlFor="ageRange">{t('forms:label_age_range')}</Label>
        <Input id="ageRange" {...register('ageRange')} placeholder="e.g., 35-45" />
        {errors.ageRange && <p className="mt-1 text-xs text-red-600">{errors.ageRange.message}</p>}
      </div>
      
      {/* Profession (Optional) */}
      <div>
          <Label htmlFor="profession">{t('forms:label_profession')} ({t('common:optional')})</Label>
          <Input id="profession" {...register('profession')} />
          {errors.profession && <p className="mt-1 text-xs text-red-600">{errors.profession.message}</p>}
      </div>

      {/* Location */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
              <Label htmlFor="location.city">{t('forms:label_city')}</Label>
              <Input id="location.city" {...register('location.city')} />
              {errors.location?.city && <p className="mt-1 text-xs text-red-600">{errors.location.city.message}</p>}
          </div>
           <div>
              <Label htmlFor="location.country">{t('forms:label_country')}</Label>
              <Input id="location.country" {...register('location.country')} />
              {errors.location?.country && <p className="mt-1 text-xs text-red-600">{errors.location.country.message}</p>}
          </div>
      </div>

      {/* Description */}
       <div>
        <Label htmlFor="description">{t('forms:label_description')}</Label>
        <Textarea id="description" rows={5} {...register('description')} />
        {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>}
      </div>

       {/* Lifestyle Interests */}
       <div>
        <Label>{t('forms:label_lifestyleInterests')}</Label>
        {renderCheckboxGroup('lifestyleInterests', lifestyleTags)}
      </div>
      
       {/* Offered Arrangement */}
      <div className="space-y-4 p-4 border rounded-md">
          <h4 className="font-medium">{t('forms:section_offeredArrangement')}</h4>
          
           {/* Support Type */}
          <div>
              <Label>{t('forms:label_supportType')}</Label>
               {renderCheckboxGroup('offeredArrangement.supportType', sdSupportTags)}
          </div>
          
          {/* Mentoring Field (Conditional) */}
          {watchSupportType?.includes('mentorship') && (
               <div>
                  <Label htmlFor="offeredArrangement.mentoringField">{t('forms:label_mentoringField')} ({t('common:optional')})</Label>
                  <Input id="offeredArrangement.mentoringField" {...register('offeredArrangement.mentoringField')} />
                  {/* Utiliser ?. pour accéder au message d'erreur */}
                  {errors.offeredArrangement?.mentoringField?.message && <p className="mt-1 text-xs text-red-600">{errors.offeredArrangement.mentoringField.message}</p>}
              </div>
          )}
          
            {/* Arrangement Type */}
            <div>
              <Label>{t('forms:label_arrangementType')}</Label>
              {renderCheckboxGroup('offeredArrangement.arrangementType', arrangementTypeTags)}
            </div>
      </div>

      {/* --- Media Upload Section --- */}
      <div className="space-y-4 p-4 border rounded-md">
          <h4 className="font-medium">{t('forms:section_photos')}</h4>
           {/* Assuming SDs only upload photos based on spec, adjust if needed */}
          <MediaUpload 
            onFilesChange={setPhotoFiles} 
            accept={{ 'image/*': ['.png', '.jpeg', '.jpg'] }}
            maxFiles={5} // Example limit
            initialFiles={initialMedia?.photos} // Pass initial photos if editing
          />
      </div>
      {/* --- End Media Upload Section --- */}

      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t('common:submitting') : t('common:save_changes')}
      </Button>
    </form>
  );
};

export default SugarDaddyProfileForm; 